/* eslint-disable @typescript-eslint/no-unnecessary-condition -- defensive reads of optional projection fields */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useStore } from '../store';
import { fmtMoney } from '../bridge';

/** P510 price history panel (Office > Market prices). Canvas line chart per
 *  species from the ui.price-history projection (Sim-Core PR #389): one
 *  reference-price point per species per in-game day. The series cap is a
 *  flagged sim placeholder (90 days). No new assets - canvas drawing only. */

const CHART_H = 180;
const PAD = { l: 8, r: 8, t: 10, b: 22 };

const fmtSpecies = (id: string) => id.replace(/_/g, ' ');

function drawChart(canvas: HTMLCanvasElement, points: Array<{ day: number; priceCents: number }>): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = canvas.clientWidth;
  const h = CHART_H;
  if (w === 0) return;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  const css = getComputedStyle(canvas);
  const ink = css.getPropertyValue('--ui-ink').trim() || '#2b2118';
  const faint = css.getPropertyValue('--ui-ink-faint').trim() || '#c9bda6';
  const accent = css.getPropertyValue('--ui-accent').trim() || '#7a4a1f';
  ctx.clearRect(0, 0, w, h);

  const plotW = w - PAD.l - PAD.r;
  const plotH = h - PAD.t - PAD.b;
  const days = points.map((p) => p.day);
  const prices = points.map((p) => p.priceCents);
  const dMin = Math.min(...days);
  const dMax = Math.max(...days);
  let pMin = Math.min(...prices);
  let pMax = Math.max(...prices);
  if (pMin === pMax) { pMin = Math.max(0, pMin - 1); pMax = pMax + 1; }
  const x = (d: number) => PAD.l + (dMax === dMin ? plotW / 2 : ((d - dMin) / (dMax - dMin)) * plotW);
  const y = (p: number) => PAD.t + (1 - (p - pMin) / (pMax - pMin)) * plotH;

  // Grid: three horizontal bands + min/max price labels.
  ctx.strokeStyle = faint;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 4]);
  for (const frac of [0, 0.5, 1]) {
    const gy = PAD.t + frac * plotH;
    ctx.beginPath();
    ctx.moveTo(PAD.l, gy);
    ctx.lineTo(w - PAD.r, gy);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.fillStyle = ink;
  ctx.font = '10px sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(`$${fmtMoney(pMax)}`, PAD.l, PAD.t - 2 < 8 ? 8 : PAD.t - 2 + 4);
  ctx.textAlign = 'right';
  ctx.fillText(`$${fmtMoney(pMin)}`, w - PAD.r, PAD.t + plotH - 4);
  ctx.textAlign = 'left';
  ctx.fillText(`day ${dMin}`, PAD.l, h - 8);
  ctx.textAlign = 'right';
  ctx.fillText(`day ${dMax}`, w - PAD.r, h - 8);

  // Series line.
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  points.forEach((p, i) => {
    if (i === 0) ctx.moveTo(x(p.day), y(p.priceCents));
    else ctx.lineTo(x(p.day), y(p.priceCents));
  });
  ctx.stroke();

  // Latest point marker.
  const last = points[points.length - 1];
  if (last) {
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(x(last.day), y(last.priceCents), 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function PriceHistoryPanel() {
  const { sim } = useStore();
  const history = sim.priceHistory;
  const speciesIds = useMemo(() => Object.keys(history?.series ?? {}).sort(), [history]);
  const [selected, setSelected] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const active = selected !== null && speciesIds.includes(selected) ? selected : speciesIds[0] ?? null;
  const points = active ? history?.series[active] ?? [] : [];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || points.length === 0) return;
    const redraw = () => { drawChart(canvas, points); };
    redraw();
    const observer = new ResizeObserver(redraw);
    observer.observe(canvas);
    return () => { observer.disconnect(); };
  }, [points]);

  if (!history) {
    return (
      <div style="padding:6px 14px 16px">
        <div class="eyebrow eyebrow-rule">Market prices</div>
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:8px">
          This worker has not provided recorded price history. No prices are estimated.
        </p>
      </div>
    );
  }

  const first = points[0];
  const last = points[points.length - 1];
  const delta = first && last ? last.priceCents - first.priceCents : 0;

  return (
    <div style="padding:6px 14px 16px">
      <div class="eyebrow eyebrow-rule">Market prices - history</div>

      {speciesIds.length === 0 && (
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:10px">No price points recorded yet - history builds one point per species per day.</p>
      )}

      {speciesIds.length > 0 && (
        <>
          <div style="display:flex;gap:6px;overflow-x:auto;margin-top:10px;padding-bottom:4px;-webkit-overflow-scrolling:touch">
            {speciesIds.map((id) => (
              <button
                key={id}
                type="button"
                class={id === active ? 'btn btn-primary' : 'btn btn-quiet'}
                style="flex:0 0 auto;min-height:44px;padding:10px 14px;font-size:12px;text-transform:capitalize"
                onClick={() => { setSelected(id); }}
              >
                {fmtSpecies(id)}
              </button>
            ))}
          </div>

          {active && points.length > 0 && (
            <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:10px;padding:10px 12px">
              <div class="row" style="margin:0;border:none;padding:0;flex-wrap:wrap;gap:6px">
                <span class="row-main" style="flex-basis:100%">
                  <div class="row-label" style="text-transform:capitalize;white-space:normal">{fmtSpecies(active)} - reference price</div>
                </span>
                <span class="row-meta" style="font-size:12px;text-align:left;white-space:normal">
                  <div>${fmtMoney(last?.priceCents ?? 0)}</div>
                  <div class="row-label" style={{ color: delta > 0 ? 'var(--ui-green, #3e7a3e)' : delta < 0 ? 'var(--ui-red, #a03428)' : undefined }}>
                    {points.length === 1 ? '1 recorded point - no trend yet' : `${delta === 0 ? 'flat' : `${delta > 0 ? '+' : '-'}$${fmtMoney(Math.abs(delta))}`} across ${String(points.length)} recorded points`}
                  </div>
                </span>
              </div>
              <canvas
                ref={canvasRef}
                style={{ width: '100%', height: `${CHART_H}px`, marginTop: '8px', display: 'block' }}
                role="img"
                aria-label={`Price history chart for ${fmtSpecies(active)}: ${points.length} daily points, latest $${fmtMoney(last?.priceCents ?? 0)}`}
              />
              <div class="row-label" style="margin-top:6px;text-transform:none;letter-spacing:0.01em;white-space:normal">
                Historical stock reference for a healthy just-adult animal: dealer commission, current ranch-path modifier, demand events, saturation and season included. No individual coat or buyer premium, transport costs or bred-animal research bonus. Not a sale quote. Latest point: game day {last?.day}. History retains up to {history.days} daily points per species.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
